import { Options, YAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighStock from 'highcharts/highstock';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import { Currency, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import formattingService, { ThisContext } from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { EventActionsEnum, EventCategoriesEnum, EventDataTargetsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import {
    chartColors,
    ChartContext,
    columnChartType,
    defaultChartAxisTitleStyle,
    defaultChartOptions,
    HighchartsPlot,
    IChartBarDataSeries,
    IChartDataSeries,
    IChartProps,
    lineChartType,
} from '../ChartDefinitions';
import chartService from '../ChartService';

export interface IStackedBarLineChartRawProps extends IChartProps {
    barsSeries: IChartBarDataSeries[];
    linesSeries?: IChartDataSeries[];
    yAxisLabel?: string;
    volumeAxisLabel?: string;
    unitOfMeasure?: UnitOfMeasure;
    currency?: Currency;
    useVerticalSeparation?: boolean;
    barRegionHeightPercent?: number;
    lineRegionHeightPercent?: number;
    showLineMarkers?: boolean;
}

const defaultLabelRadius = 12;
const defaultLineWidth = 1;
const percentValueOffset = -30;
const defaultBarRegionHeightPercent = 60;
const defaultLineRegionHeightPercent = 40;
const barRegionTickAmount = 7;
const lineRegionTickAmount = 4;

const StackedBarLineChartRaw: React.FC<IStackedBarLineChartRawProps> = (props: IStackedBarLineChartRawProps) => {
    // Application hooks.
    const [translations] = useLanguage();

    // Chart hooks.
    const stackedBarLineChartDefaultOptions = useMemo<Options>(() => {
        const barRegionHeight = props.barRegionHeightPercent ?? defaultBarRegionHeightPercent;
        const lineRegionHeight = props.lineRegionHeightPercent ?? defaultLineRegionHeightPercent;
        const defaultVolumeAxisTitle = `${translations.words.volume} (${chartService.getCurrencyAndUnitOfMeasureText(props.unitOfMeasure)})`;
        const volumeAxisTitle = props.volumeAxisLabel ?? props.yAxisLabel ?? defaultVolumeAxisTitle;
        const priceAxisTitle =
            props.yAxisLabel ?? `${translations.words.price} (${chartService.getCurrencyAndUnitOfMeasureText(props.unitOfMeasure, props.currency)})`;

        const yAxis: YAxisOptions[] = props.useVerticalSeparation
            ? [
                  {
                      title: { text: priceAxisTitle, style: defaultChartAxisTitleStyle },
                      opposite: true,
                      height: `${lineRegionHeight}%`,
                      tickAmount: lineRegionTickAmount,
                  },
                  {
                      title: { text: volumeAxisTitle, style: defaultChartAxisTitleStyle },
                      top: `${lineRegionHeight}%`,
                      height: `${barRegionHeight}%`,
                      tickAmount: barRegionTickAmount,
                  },
              ]
            : [
                  {
                      title: { text: volumeAxisTitle, style: defaultChartAxisTitleStyle },
                  },
                  {
                      title: { text: priceAxisTitle, style: defaultChartAxisTitleStyle },
                      opposite: true,
                  },
              ];

        return { ...defaultChartOptions, yAxis, series: [] };
    }, [props.useVerticalSeparation, props.barRegionHeightPercent, props.lineRegionHeightPercent]);

    const [highchartOptions, setHighchartOptions] = useState<Options>(stackedBarLineChartDefaultOptions);

    const barsYAxisIndex = props.useVerticalSeparation ? 1 : 0;
    const linesYAxisIndex = props.useVerticalSeparation ? 0 : 1;

    const getDataSeriesDefinitions = useCallback(() => {
        const barsDataSeries = props.barsSeries.flatMap((barSeries, index) => {
            const currentColor = chartColors.stackedBarChartColorsRuleSetOne[index];

            return [
                {
                    name: barSeries.label,
                    type: columnChartType,
                    data: [] as HighchartsPlot[],
                    yAxis: barsYAxisIndex,
                    marker: {
                        symbol: 'square',
                        radius: defaultLabelRadius,
                        fillColor: currentColor,
                        lineWidth: defaultLineWidth,
                        lineColor: currentColor,
                    },
                    stack: barSeries.stack,
                    column: {},
                    events: {
                        legendItemClick() {
                            loggingService.trackEventWithAnalytics(
                                EventActionsEnum.ButtonClick,
                                EventCategoriesEnum.LegendItemClicked,
                                barSeries.label,
                                EventDataTargetsEnum.StackedBarLineChart,
                            );
                        },
                    },
                    color: currentColor,
                    visible: true,
                    showInLegend: true,
                    dataLabels: {},
                },
            ];
        });

        const lineDataSeries = props.linesSeries?.flatMap((lineSeries, index) => {
            const currentColor = chartColors.lineChartColorsRuleSetOne[index];

            return [
                {
                    name: lineSeries.label,
                    yAxis: linesYAxisIndex,
                    marker: {
                        enabled: props.showLineMarkers ?? false,
                    },
                    data: [] as HighchartsPlot[],
                    color: currentColor,
                    type: lineChartType,
                    visible: true,
                    showInLegend: true,
                    dataLabels: {},
                    dashStyle: lineSeries.dashStyle,
                },
            ];
        });

        return [...barsDataSeries, ...(lineDataSeries ?? [])];
    }, [props.barsSeries, props.linesSeries, barsYAxisIndex, linesYAxisIndex]);

    useEffect(() => {
        const dataSeries = getDataSeriesDefinitions();
        const allSeries = [...props.barsSeries, ...(props.linesSeries ?? [])];

        const downloadData = chartService.getDownloadData(allSeries);

        allSeries.forEach((series, index) => {
            const endIndex = series!.data?.length;
            const currentSeries = series as IChartBarDataSeries;

            if (index < props.barsSeries.length && currentSeries.data[index]?.percentValue) {
                dataSeries[index].dataLabels = {
                    enabled: true,
                    formatter() {
                        const thisContext = this as ThisContext;
                        const percentValue = currentSeries.data[thisContext.point.index]?.percentValue;
                        return percentValue !== undefined ? formattingService.formatPercentNumber(percentValue) : null;
                    },
                    verticalAlign: 'top',
                    y: percentValueOffset,
                    style: { color: scssVariables.mainTextColor },
                };
            }

            dataSeries[index].data = series.data?.slice(0, endIndex).map((item) => ({
                x: item.asOfDate.getTime(),
                y: item.value,
                customLabel: item.customLabel,
            }));
        });

        const hasCustomLabels = allSeries.some((series) => series.data?.some((item) => item.customLabel));

        const customLabelMap = new Map<number, string>();
        if (hasCustomLabels) {
            allSeries.forEach((series) => {
                series.data?.forEach((item) => {
                    if (item.customLabel) {
                        customLabelMap.set(item.asOfDate.getTime(), item.customLabel);
                    }
                });
            });
        }

        const barTimestamps = Array.from(new Set(props.barsSeries.flatMap((barSeries) => barSeries.data?.map((item) => item.asOfDate.getTime()) ?? []))).sort(
            (a, b) => a - b,
        );

        const barPointRange =
            barTimestamps.length >= 2
                ? barTimestamps.reduce((minimumGap, timestamp, index) => {
                      if (index === 0) {
                          return minimumGap;
                      }
                      const gap = timestamp - barTimestamps[index - 1];
                      return gap < minimumGap ? gap : minimumGap;
                  }, Infinity)
                : undefined;

        const columnPlotOptions: any = {
            stacking: 'normal',
            dataLabels: {
                enabled: false,
            },
            ...(barPointRange !== undefined && { pointRange: barPointRange }),
        };

        const allTimestamps = dataSeries.flatMap((series) => (series.data as HighchartsPlot[])?.map((point) => point.x) ?? []).filter(Boolean);
        const xAxisMin = allTimestamps.length > 0 ? Math.min(...allTimestamps) : undefined;
        const xAxisMax = allTimestamps.length > 0 ? Math.max(...allTimestamps) : undefined;

        const newOptions = {
            ...stackedBarLineChartDefaultOptions,
            plotOptions: {
                column: columnPlotOptions,
            },
            xAxis: {
                type: 'datetime',
                ...(xAxisMin !== undefined && { min: xAxisMin }),
                ...(xAxisMax !== undefined && { max: xAxisMax }),
                ...(hasCustomLabels && {
                    tickPositions: Array.from(customLabelMap.keys()),
                    labels: {
                        formatter(): string {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const axisContext = this as any;
                            const tickValue = axisContext.value;
                            const customLabel = customLabelMap.get(tickValue);
                            if (customLabel) {
                                return customLabel;
                            }

                            return axisContext.axis.defaultLabelFormatter.call(axisContext);
                        },
                    },
                }),
            },
            ...{ series: dataSeries },
            ...{
                tooltip: {
                    formatter() {
                        const context = this as unknown as ChartContext;
                        const pointOptions = context.point?.options as HighchartsPlot | undefined;
                        const customLabel = pointOptions?.customLabel;

                        return chartService.getTooltipText(context, {
                            displayDecimalPlacesMinimum: props.displayDecimalPlacesMinimum ?? 0,
                            displayDecimalPlacesMaximum: props.displayDecimalPlacesMaximum ?? 0,
                            customDateLabel: customLabel,
                        });
                    },
                },
            },
            downloadData,
        };

        setHighchartOptions(newOptions as Options);
    }, [props.linesSeries, props.barsSeries]);

    return <HighchartsReact ref={props.chartReference} highcharts={HighStock} options={highchartOptions} containerProps={{ style: { height: '100%' } }} />;
};

export default memo(StackedBarLineChartRaw);
